import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import api from '../services/api'
import Utils from '../utils'

import moment from 'moment'
import useDirtyPrompt from '../hooks/useDirtyPrompt'
import useModal from '../hooks/useModal'
import theme from '../styles/theme'

const TimerBox = styled.div`
    margin-top: 20px;
    padding: 20px;
    border: 2px dashed #aaa;
    border-radius: 10px;
    background-color: #fffefc;
    display: flex;
    flex-direction: column;
    align-items: center;
`

const TimeText = styled.div`
    font-size: 2em;
    font-weight: bold;
    margin-bottom: 15px;
`

const ButtonGroup = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 10px;
`

const Button = styled.button.attrs(props => ({
  variant: props.variant || 'default'
}))`
    padding: 8px 15px;
    border: ${({ variant }) =>
            variant === 'save' ? `2px solid #001054` : 'none'};
    border-radius: 5px;
    font-size: 1em;
    font-weight: bold;
    color: ${({ variant }) => 
            variant === 'reset' ? '#fff' :
                    variant === 'save' ? theme.colors.primary :
                            '#fff'};
    background-color: ${({ variant }) => 
            variant === 'start' ? theme.colors.primary :
                    variant === 'pause' ? '#FFA500' :
                            variant === 'reset' ? theme.colors.secondary :
                                    variant === 'save' ? 'transparent' :
                                            '#eee'};
    cursor: pointer;

    &:hover {
        opacity: 0.9;
        background-color: ${({ variant }) =>
                variant === 'save' ? theme.colors.skyblue : ''};
    }

    &:disabled {
        background-color: ${props => props.theme.colors.lightGray};
        color: ${props => props.theme.colors.gray};
        cursor: not-allowed;
    }
`

const Input = styled.input`
    padding: 8px;
    margin-top: 10px;
    font-size: 1em;
    border: 1px solid #ccc;
    border-radius: 5px;
    width: 250px;
`

function formatSeconds(sec) {
  const h = String(Math.floor(sec / 3600)).padStart(2, '0')
  const m = String(Math.floor((sec % 3600) / 60)).padStart(2, '0')
  const s = String(sec % 60).padStart(2, '0')
  return `${h}:${m}:${s}`
}

export default function Timer({ logOn, setDailyLogContentsList, setStatics }) {
  const [focusTimeSec, setfocusTimeSec] = useState(0)
  const [isRunning, setIsRunning] = useState(false)
  const [contents, setContents] = useState('')
  const [startOn, setStartOn] = useState(null)
  const intervalRef = useRef(null)
  const startTimeRef = useRef(null)
  const { openModal } = useModal()
  const { updateIsDirty } = useDirtyPrompt("작성 중인 내용이 사라질 수 있습니다. 이동하시겠습니까?")

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        const now = Date.now()
        const elapsedSec = Math.floor((now - startTimeRef.current) / 1000)
        setfocusTimeSec(elapsedSec)
      }, 1000)
    } else {
      clearInterval(intervalRef.current)
    }

    return () => clearInterval(intervalRef.current)
  }, [isRunning])

  const handleStartPause = () => {
    if (!isRunning) {
      const now = Date.now()
      if (focusTimeSec === 0) {
        startTimeRef.current = now
        setStartOn(moment(now).format('YYYY-MM-DD HH:mm:ss'))
      } else {
        startTimeRef.current = now - focusTimeSec * 1000
      }
    }
    setIsRunning(prev => !prev)
  }

  const handleReset = () => {
    setIsRunning(false)
    setfocusTimeSec(0)
    setContents('')
    setStartOn(null)
    startTimeRef.current = null
    updateIsDirty('') // reset 시 더티 해제
  }

  const handleSave = async () => {
    if (!contents.trim()) {
      openModal({
        type: 'alert',
        props: { contents: '스터디 내용을 입력해주세요.', isError: true }
      })
      return
    }

    if (!startOn) {
      openModal({
        type: 'alert',
        props: { contents: '시작 시간이 유효하지 않습니다.', isError: true }
      })
      return
    }

    if (focusTimeSec <= 60) {
      openModal({
        type: 'alert',
        props: { contents: '집중 시간이 너무 짧습니다.', isError: true }
      })
      return
    }

    await api.post(`/daily-logs/${logOn}`, {
      startOn,
      endOn: moment().format('YYYY-MM-DD HH:mm:ss'),
      contents,
      focusTimeSec,
      dailyLogTypeId: Utils.getDailyLogTypeIdByTypeNameEn('study'),
    }).then(result => {
      setDailyLogContentsList(result.data.dailyLogContentsList)
      handleReset()
    }).catch(e => {
      console.error(e)
      openModal({
        type: 'alert',
        props: { contents: e.response?.data.message || e.message, isError: true }
      })
    })

    const reportTypeId = await api.get(`/reports/infos/types/${logOn}`)
      .then(result => result.data.reportTypeList[0].reportTypeId || '')
      .catch(e => {
        console.error(e)
        openModal({ type: 'alert', props: { isError: true, contents: e.response?.data.message || e.message } })
      })

    await api.get(`/reports/infos/performances/${logOn}`, { params: { reportTypeId }})
      .then(result => { setStatics(result.data.statics) })
      .catch(e => {
        console.error(e)
        openModal({ type: 'alert', props: { isError: true, contents: e.response?.data.message || e.message } })
      })
  }

  useEffect(() => {
    updateIsDirty(contents, focusTimeSec > 0 ? focusTimeSec : '', startOn)
  }, [contents, focusTimeSec, startOn, updateIsDirty])

  return (
    <TimerBox>
      <TimeText>{formatSeconds(focusTimeSec)}</TimeText>
      <ButtonGroup>
        <Button onClick={handleStartPause} variant={isRunning ? 'pause' : 'start'}>
          {isRunning ? '일시정지' : '시작'}
        </Button>
        <Button onClick={handleReset} variant="reset">리셋</Button>
        <Button onClick={handleSave} variant="save" disabled={isRunning || focusTimeSec === 0}>
          저장
        </Button>
      </ButtonGroup>
      <Input
        type="text"
        placeholder='스터디 내용'
        value={contents}
        onChange={(e) => setContents(e.target.value)}
        disabled={isRunning}
      />
    </TimerBox>
  )
}
